import { MapPin, Users } from 'lucide-react';
import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { Participant } from '../../../interfaces/reservation.interface';
import {
  getBookingModeLabel,
  getTypeLabel,
} from '../../../utils/workshopMigration';
import { reservationPlaces } from '../../../utils/reservation';

interface Props {
  workshop: WorkshopV2;
  participants: Participant[];
  /** Les réservations arrivent après les ateliers : on l'indique sans bloquer la carte. */
  participantsLoading: boolean;
  /** Lecture des réservations impossible : ne pas annoncer « aucun participant ». */
  participantsUnavailable: boolean;
  onOpen: () => void;
}

/** Nombre de noms affichés en aperçu avant le « +N ». */
const PREVIEW_COUNT = 3;

const PastWorkshopCard = ({
  workshop,
  participants,
  participantsLoading,
  participantsUnavailable,
  onOpen,
}: Props) => {
  const date = workshop.date.toDate().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const places = participants.reduce((sum, p) => sum + reservationPlaces(p), 0);
  const capacity = workshop.maxParticipants || 0;
  const fillRate = capacity > 0 ? Math.round((places / capacity) * 100) : 0;

  const preview = participants.slice(0, PREVIEW_COUNT);
  const extra = participants.length - preview.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left flex items-center justify-between gap-4 bg-white p-4 rounded shadow-sm border border-[#623413]/10 hover:border-[#623413]/30 hover:shadow transition"
    >
      <div className="flex items-center gap-4 min-w-0">
        <img
          src={workshop.imageUrl || '/defaultimages/defaultcake.png'}
          alt={workshop.title}
          className="h-16 w-16 shrink-0 object-cover rounded border border-[#623413]/20"
        />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-base font-semibold text-[#623413] truncate">
              {workshop.title}
            </p>
            <span className="shrink-0 rounded-full bg-[#623413]/5 px-2 py-0.5 text-xs text-[#623413]">
              {getTypeLabel(workshop.type)}
            </span>
          </div>
          <p className="text-sm text-gray-600 first-letter:uppercase">{date}</p>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            {workshop.location && (
              <>
                <MapPin size={13} className="shrink-0" />
                <span className="truncate">{workshop.location}</span>
                <span aria-hidden>—</span>
              </>
            )}
            <span className="shrink-0">
              {getBookingModeLabel(workshop.bookingMode)}
            </span>
          </p>

          {participantsLoading ? (
            <p className="mt-1 text-xs text-gray-400">
              Chargement des participants…
            </p>
          ) : participantsUnavailable ? (
            <p className="mt-1 text-xs text-gray-400">
              Participants indisponibles
            </p>
          ) : participants.length > 0 ? (
            <p className="mt-1 text-xs text-gray-500 truncate">
              {preview.map((p) => p.displayName).join(', ')}
              {extra > 0 && ` +${extra}`}
            </p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">Aucun participant</p>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0 w-40">
        <p className="flex items-center gap-1.5 text-sm font-medium text-[#623413]">
          <Users size={15} />
          {participantsUnavailable ? '—' : places}
          {capacity > 0 && (
            <span className="font-normal text-gray-400">/ {capacity}</span>
          )}
        </p>
        {capacity > 0 && !participantsUnavailable && (
          <>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-[#623413]"
                style={{ width: `${Math.min(100, fillRate)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400">{fillRate} % de remplissage</p>
          </>
        )}
      </div>
    </button>
  );
};

export default PastWorkshopCard;
